import React, { useState } from 'react';
import ReactEDSWrapper from '../../../utils/react-eds-wrapper';
import { Button, Tag } from '@dsi/react-eds';
import { TestSummary } from '../TestExecution/const/TestSummary';
import { useSwobNotifications } from '../../../hooks/NotificationsProvider';
import { useLoader } from '../../../hooks/useLoader';
import { fetchAllTestCases, exportTestExecutionSummaryToCSV } from '../../../utils/testExecutionCSVExporter';
import { downloadCSV } from '../../../utils/csvExporter';
import { AxiosError } from 'axios';
import styles from './TestExecutionSummaryMetadata.module.css';

interface TestExecutionSummaryMetadataProps {
  testSummary: TestSummary;
  testExecutionId: string;
}

const TestExecutionSummaryMetadata: React.FC<TestExecutionSummaryMetadataProps> = ({ testSummary, testExecutionId }) => {
  const { addNotifications } = useSwobNotifications();
  const { showLoader, hideLoader } = useLoader();
  const [isExporting, setIsExporting] = useState(false);

  const handleDownloadSummary = async () => {
    if (isExporting) {
      return; // Prevent multiple clicks
    }

    setIsExporting(true);
    showLoader();

    try {
      // Fetch all test cases
      const allTestCases = await fetchAllTestCases(testExecutionId);

      // Generate CSV content
      const csvContent = exportTestExecutionSummaryToCSV(testSummary, allTestCases);

      // Generate filename with timestamp
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
      const filename = `test-execution-${testExecutionId}-${timestamp}`;

      // Download CSV file
      downloadCSV(csvContent, filename);

      // Show success notification
      addNotifications([
        {
          title: 'Success',
          icon: 'icon-success',
          description: `CSV file downloaded successfully with ${allTestCases.length} test cases`,
          timestamp: new Date(),
          color: 'green'
        }
      ]);
    } catch (error: unknown) {
      // Show error notification
      const errorMessage =
        error && error instanceof AxiosError
          ? error.response?.data?.message || error.message
          : 'An error occurred while exporting the summary';
      addNotifications([
        {
          title: 'Export Error',
          icon: 'icon-triangle-warning',
          description: errorMessage,
          timestamp: new Date(),
          color: 'red'
        }
      ]);
    } finally {
      setIsExporting(false);
      hideLoader();
    }
  };

  return (
    <div className={styles.metadataContainer}>
      <div className={styles.tagsContainer}>
        <ReactEDSWrapper component={Tag} big color="grey">
          {testSummary.test_collection_name}
        </ReactEDSWrapper>
        <ReactEDSWrapper component={Tag} big color="grey">
          {testSummary.workflow_id}
        </ReactEDSWrapper>
        <ReactEDSWrapper component={Tag} big color="purple">
          {testSummary.product_version}
        </ReactEDSWrapper>
        <ReactEDSWrapper component={Tag} big color="purple">
          {testSummary.product_number}
        </ReactEDSWrapper>
        <ReactEDSWrapper component={Tag} big color="blue">
          {testSummary.start_timestamp}
        </ReactEDSWrapper>
        <ReactEDSWrapper component={Tag} big color="blue">
          {testSummary.duration}
        </ReactEDSWrapper>
      </div>
      <div className={styles.downloadButtonContainer}>
        <ReactEDSWrapper component={Button} icon="download-save" onClick={handleDownloadSummary} disabled={isExporting}>
          {isExporting ? 'Exporting...' : 'Download Summary'}
        </ReactEDSWrapper>
      </div>
    </div>
  );
};

export default TestExecutionSummaryMetadata;
